import { isEqual, omit } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import useReactRouter from 'use-react-router';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import ConnectionModel, {
  ConnectionKind,
  ConnectionResponseType,
} from '@mm/state/model/ConnectionModel';
import { ConnectionTagModel } from '@mm/state/model/ConnectionTagModel';
import {
  ConnectionVariableKind,
  ConnectionVariableModel,
} from '@mm/state/model/ConnectionVariableModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import EditConnectionAttributes from './EditConnectionAttributes/EditConnectionAttributes';
import EditConnectionHeader from './EditConnectionHeader/EditConnectionHeader';
import './EditConnectionPanel.pcss';
import EditConnectionType from './EditConnectionType/EditConnectionType';
import EditRequestTypeAndURL from './EditRequestTypeAndURL/EditRequestTypeAndURL';
import { EditResponseMapping } from './EditResponseMapping/EditResponseMapping';
import EditResponseType from './EditResponseType/EditResponseType';
import { blankVariable, canBeTested } from './helpers';
import PrimarySelect from '../../../../../common/components/PrimarySelect/PrimarySelect';
import TextInput from '../../../../../common/components/TextInput';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import VariableHelpers from '../../../../../common/helpers/variable-helpers';

type Props = {
  isSlideout?: boolean;
  connectionIdFromSlideout?: number;
  reloadConnections?: () => void;
};

export const useEditConnectionActions = () => {
  const connectionCRUDActions = useConnectionCRUDActions();

  const { facebookPage, chatbot } = useSelector(
    (state: StoreState) => ({
      facebookPage: activeFacebookPageSelector(state),
      chatbot: activeChatbotSelector(state),
    }),
    shallowEqual,
  );

  const updateRemoteConnection = async (connection: ConnectionModel) => {
    if (facebookPage && connection) {
      const variables = connection.variables || [];

      const filteredVariables = variables.filter(variable => {
        if (
          isEqual(
            omit(variable, ['static', 'kind']),
            omit(blankVariable(), ['static', 'kind']),
          )
        ) {
          return false;
        }

        return variable.static ? true : !!variable.name;
      });

      return connectionCRUDActions.update(facebookPage.id, {
        ...connection,
        variables: filteredVariables,
      });
    }
  };

  return {
    updateRemoteConnection,
    facebookPage,
    chatbot,
  };
};

export const tagsFormatOptions = [
  { id: 'object', name: 'Object' },
  { id: 'array', name: 'Array' },
];

const EditConnectionPanel = (props: Props) => {
  const { isSlideout, connectionIdFromSlideout, reloadConnections } = props;
  const { updateRemoteConnection, facebookPage, chatbot } =
    useEditConnectionActions();

  const {
    match: {
      params: { connectionId },
    },
  } = useReactRouter<{ connectionId: string }>();

  const [connection, setConnection] = useState<ConnectionModel>();
  const [variablesToSend, setVariablesToSend] = useState<
    ConnectionVariableModel[]
  >([]);
  const [variablesToReceive, setVariablesToReceive] = useState<
    ConnectionVariableModel[]
  >([]);
  const connectionCRUDActions = useConnectionCRUDActions();
  const { openTestConnectionModal } = useModalActions();

  const [error, setError] = useState('');

  const tagsFormatValue = useMemo(() => {
    return connection?.tagsField ? tagsFormatOptions[1] : tagsFormatOptions[0];
  }, [connection?.tagsField]);

  useEffect(() => {
    const getConnection = async () => {
      if (facebookPage) {
        const remoteConnection = await connectionCRUDActions.retrieveById(
          facebookPage.id,
          isSlideout ? Number(connectionIdFromSlideout) : Number(connectionId),
        );

        if (remoteConnection) {
          const connectionVariables = remoteConnection.variables || [];
          const variablesToSend = connectionVariables.filter(
            (variable: ConnectionVariableModel) =>
              variable.kind !== ConnectionVariableKind.toReceive,
          );
          const variablesToReceive = connectionVariables.filter(
            (variable: ConnectionVariableModel) =>
              variable.kind === ConnectionVariableKind.toReceive,
          );
          const initialVariablesToReceive =
            variablesToReceive.length > 0
              ? variablesToReceive
              : [blankVariable(ConnectionVariableKind.toReceive)];

          setVariablesToSend(variablesToSend);
          setVariablesToReceive(initialVariablesToReceive);
          setConnection(remoteConnection);
        }
      }
    };

    getConnection();
  }, []);

  const updateConnection = async (updatedFields: Partial<ConnectionModel>) => {
    if (!connection) {
      return;
    }
    const updatedConnection = await updateRemoteConnection({
      ...connection,
      ...updatedFields,
    });

    if (updatedConnection) {
      const connectionVariables = updatedConnection.variables || [];
      const variablesToSend = connectionVariables.filter(
        (variable: ConnectionVariableModel) =>
          variable.kind !== ConnectionVariableKind.toReceive,
      );

      setVariablesToSend(variablesToSend);
      setConnection(updatedConnection);
    }
  };

  const onNewMappingFound = (pathNames: string[]) => {
    setVariablesToReceive([
      ...variablesToReceive,
      ...pathNames.map(name => ({
        ...blankVariable(ConnectionVariableKind.toReceive),
        receivedAs: name,
      })),
    ]);
  };

  const onClickTestConnection = () => {
    if (!connection) {
      return;
    }
    const { url } = connection;
    const urlVariables = VariableHelpers.getVariablesFromString(url);

    openTestConnectionModal({
      botId: chatbot.id,
      connection,
      connectedPageId: facebookPage?.id || -1,
      url,
      variables: variablesToSend,
      urlVariables,
      mappedVariables: variablesToReceive,
      onNewMappingFound,
      shouldMapResponse:
        connection.responseType === ConnectionResponseType.SAVE_ATTRIBUTES,
    });
  };

  const onTagsChange = (tags: ConnectionTagModel[]) =>
    updateConnection({ tags });
  if (connection && facebookPage) {
    const { tags } = connection;
    return (
      <div className="edit-connection-panel">
        <EditConnectionHeader
          connection={connection}
          chatbotId={chatbot.id}
          facebookPage={facebookPage}
          onActiveChange={() =>
            updateConnection({ active: !connection?.active })
          }
          onNameChange={name => updateConnection({ name })}
          onDescriptionChange={description => updateConnection({ description })}
          onClickTestConnection={onClickTestConnection}
          canBeTested={canBeTested(connection)}
          isTemplateBot={!!chatbot.template}
          isSlideout={isSlideout}
          reloadConnections={reloadConnections}
        />

        <CollapsibleContentContainer
          orderNumber="1"
          containerName="Connection Settings"
        >
          <EditConnectionType
            connection={connection}
            chatbotId={chatbot.id}
            isSlideout={isSlideout}
            onAudienceChange={audienceId => updateConnection({ audienceId })}
            onCancel={() => updateConnection({ audienceId: null })}
            onKindChange={kind => {
              let { responseType } = connection;
              if (
                kind === ConnectionKind.AUDIENCE &&
                responseType === ConnectionResponseType.MESSENGER_JSON
              ) {
                // Audience type doesn't support messenger json response type.
                responseType = ConnectionResponseType.NONE;
              }
              updateConnection({ kind, responseType });
            }}
          />
          <div className="edit-connection-panel__devider" />

          <EditRequestTypeAndURL
            connection={connection}
            onRequestTypeChange={requestType =>
              updateConnection({ requestType })
            }
            onUrlChange={url => updateConnection({ url })}
          />
        </CollapsibleContentContainer>

        <CollapsibleContentContainer
          orderNumber="2"
          containerName={
            connection.requestType === 'post'
              ? 'Include attributes as form data'
              : 'Include attributes as URL Parameters'
          }
        >
          <div className="edit-connection-panel__attributes-settings">
            <EditConnectionAttributes
              tags={tags}
              variablesToSend={variablesToSend}
              facebookPageId={facebookPage.id}
              onVariablesChange={nextVariablesToSend =>
                updateConnection({
                  variables: [...nextVariablesToSend, ...variablesToReceive],
                })
              }
              onTagsChange={onTagsChange}
            />

            <div className="edit-connection-panel__format">
              {!!connection.tags?.length && (
                <div className="edit-connection-panel__option">
                  <div className="edit-connection-panel__title">
                    How tags should be sent
                  </div>
                  <>
                    <PrimarySelect
                      options={tagsFormatOptions}
                      value={tagsFormatValue}
                      onChange={opt => {
                        updateConnection({
                          tagsField: opt.id === 'array' ? 'tags' : null,
                        });
                        setError('');
                      }}
                      customDropdownStyles={{ zIndex: '1000003' }}
                    />
                  </>
                </div>
              )}
              {tagsFormatValue.id === 'array' && (
                <div className="edit-connection-panel__option">
                  <div className="edit-connection-panel__title">Name</div>
                  <TextInput
                    error={!!error}
                    onChange={e => {
                      setError(
                        e.target.value
                          ? ''
                          : 'Tag field name should not be empty',
                      );
                    }}
                    onBlur={e => {
                      const { value } = e?.target;

                      if (!value) {
                        return;
                      }

                      updateConnection({
                        tagsField: e?.target.value,
                      });
                    }}
                    defaultValue={connection.tagsField || undefined}
                  />
                  {error && (
                    <span className="edit-connection-panel__option-error">
                      {error}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="edit-connection-panel__options">
              <EditResponseType
                connection={connection}
                onResponseTypeChange={responseType =>
                  updateConnection({ responseType })
                }
              />
            </div>
          </div>
        </CollapsibleContentContainer>
        {connection.responseType === ConnectionResponseType.SAVE_ATTRIBUTES && (
          <CollapsibleContentContainer
            orderNumber="3"
            containerName="Map Response data to MobileMonkey attributes"
          >
            <div>
              <EditResponseMapping
                variablesToReceive={variablesToReceive}
                onVariablesChange={nextVariablesToReceive => {
                  setVariablesToReceive(nextVariablesToReceive);
                  updateConnection({
                    variables: [...nextVariablesToReceive, ...variablesToSend],
                  });
                }}
                canBeTested={canBeTested(connection)}
                onClickTestConnection={onClickTestConnection}
              />
            </div>
          </CollapsibleContentContainer>
        )}
      </div>
    );
  }

  return null;
};

export default EditConnectionPanel;
